/* eslint-disable @typescript-eslint/naming-convention */
import { BehaviorSubject, Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { MarketDetectionUtil } from '~shared/utils/market-detection.util';
import { ApiConfig, NUMBER_1 } from '~shared/constants';
import type {
  IEntrepreneurInfoFrance,
  IEntrepreneurInfoNetherlands,
} from '~app/modules/account/modules/account-management/models/user-info.model';
import { IBranchDataForTierTwo, IBranchEntries } from '~app/modules/authorization/models/branch.model';
import { COUNTRY_VALUES_FOR_PREFILLING, CountryCode } from '~shared/constants/country-code.enum';
import { pick } from 'lodash';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { switchMap, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class CompanyDetailsFromIdService {
  branchData = new BehaviorSubject<IBranchEntries[]>([]);
  selectedBranchData$ = new BehaviorSubject<IBranchDataForTierTwo>(null);
  branchData$ = this.branchData.asObservable();

  companyDetailsForTierTwoNLandFR = new BehaviorSubject<IEntrepreneurInfoNetherlands | IEntrepreneurInfoFrance>(null);
  companyDetailsForTierTwo$ = this.companyDetailsForTierTwoNLandFR.asObservable();
  headerDict = {
    'country-code': MarketDetectionUtil.getCountryCode().toLowerCase(),
  };

  constructor(private httpClient: HttpClient, private featureFlagService: ActiveFeaturesConfigcatService) {}

  public getBranchesById(taxId: string): Observable<any> {
    const requestOptions = {
      headers: new HttpHeaders(this.headerDict),
    };

    const endpoint = ApiConfig.SVC_BUYER_ACCOUNT_BRANCHES_ENDPOINT_V2(taxId)

    return this.httpClient.get(endpoint, requestOptions);
  }

  public getSelectedBranchData(taxId: string, branchNumber: string): Observable<any> {
    const requestOptions = {
      headers: new HttpHeaders(this.headerDict),
    };

    let endpoint = ApiConfig.SVC_BUYER_ACCOUNT_SELECTED_BRANCH_DATA_ENDPOINT_V2(taxId, branchNumber);

    if (!branchNumber) {
      endpoint = ApiConfig.SVC_BUYER_ACCOUNT_SELECTED_BRANCH_DATA_FROM_RSIN_ENDPOINT_V2(taxId);
    }

    return this.httpClient.get(endpoint, requestOptions);
  }

  postBranchesData(branchData) {
    this.branchData.next(branchData);
  }

  clearCompanyData() {
    this.companyDetailsForTierTwoNLandFR.next(null);
  }

  postCompanyData(
    companyDetails?: IEntrepreneurInfoNetherlands | IEntrepreneurInfoFrance,
    currentCountry = MarketDetectionUtil.getCountryCode()
  ) {
    const countrySpecificFields = {
      [CountryCode.Netherlands]: ['kvkNumber'] as (keyof IEntrepreneurInfoNetherlands)[],
      [CountryCode.France]: ['sirenNumber', 'siret'] as (keyof IEntrepreneurInfoFrance)[],
    };

    const currentCountryFields = countrySpecificFields[currentCountry];
    const countrySpecificData = pick(companyDetails, currentCountryFields);

    const { taxNumber } = companyDetails as IEntrepreneurInfoFrance;
    const {
      businessName,
      branchNumber,
      legalAddressCity,
      legalAddressStreet,
      legalAddressPostalCode,
      legalAddressBuildingNumber,
      legalAddressCountry,
      externalBranch,
      externalLegalForm,
    } = companyDetails;

    // prettier-ignore
    this.companyDetailsForTierTwoNLandFR.next({ // NOSONAR
      ...countrySpecificData,
      businessName,
      branchNumber,
      legalAddressCity,
      legalAddressCountry,
      legalAddressPostalCode,
      legalAddressStreet,
      legalAddressBuildingNumber,
      taxNumber,
      externalBranch,
      externalLegalForm
    } as IEntrepreneurInfoNetherlands | IEntrepreneurInfoFrance);
  }

  clearSelectedBranchDataForTierTwo() {
    this.selectedBranchData$.next(null);
  }

  publishSelectedBranchDataForTierTwo(selectedBranchData) {
    this.selectedBranchData$.next(selectedBranchData);
  }

  formatTierTwoDataForSignup(selectedBranchData, taxId, isRsin) {
    return {
      [MarketDetectionUtil.isDutchMarket() ? 'kvkNumber' : 'sirenNumber']: taxId,
      businessName: selectedBranchData?.businessName,
      branchNumber: isRsin ? null : selectedBranchData?.branchNumber,
      legalAddressCity: selectedBranchData?.legalAddressCity,
      legalAddressCountry: MarketDetectionUtil.isDutchMarket()
        ? COUNTRY_VALUES_FOR_PREFILLING.Netherlands
        : COUNTRY_VALUES_FOR_PREFILLING.France,
      legalAddressPostalCode: selectedBranchData?.legalAddressPostalCode,
      legalAddressStreet: selectedBranchData?.legalAddressStreet,
      legalAddressBuildingNumber: selectedBranchData?.legalAddressBuildingNumber,
      taxNumber: MarketDetectionUtil.isDutchMarket() ? selectedBranchData?.taxNumber : taxId,
      siret: selectedBranchData?.siret,
      externalLegalForm: selectedBranchData?.externalLegalForm,
      externalBranch: selectedBranchData?.externalBranch,
    };
  }
}
